import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Tooltip from '../common/Tooltip';
import { foodDatabase, getConversionFactor, convertValue } from '../data/foodDatabase';
import fatSecretService from '../services/fatSecretService';

const { FiPlus, FiX, FiSearch, FiSettings, FiGlobe, FiDatabase, FiStar, FiEdit2, FiTrash2, FiInfo } = FiIcons;

const FoodSelector = ({ selectedFoods, setSelectedFoods, onStepComplete, customFoods = [], onEditCustomFood, onDeleteCustomFood, user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchSource, setSearchSource] = useState('local'); // 'local' or 'external'

  const categories = ['All', 'Protein', 'Carbohydrate', 'Fat', 'Vegetable'];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 2 && searchSource === 'external') {
        performExternalSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchSource]);

  const performExternalSearch = async () => {
    setIsSearching(true);
    try {
      const results = await fatSecretService.searchFoods(searchTerm, 20);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const allFoods = [
    ...foodDatabase,
    ...customFoods,
    ...(searchSource === 'external' ? searchResults : [])
  ];

  const filteredFoods = allFoods.filter(food => {
    const matchesSearch = food.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || food.category === selectedCategory;
    const notSelected = !selectedFoods.find(selected => selected.id === food.id);
    return matchesSearch && matchesCategory && notSelected;
  });

  // Helper to determine smart defaults based on unit
  const getDefaultRanges = (unit) => {
    const u = unit.toLowerCase();
    // Grams and Milliliters
    if (u === 'g' || u === 'ml') {
      return { min: 1, max: 500 };
    }
    // Cups
    if (u === 'cup') {
      return { min: 1, max: 5 };
    }
    // Ounces
    if (u === 'oz') {
      return { min: 1, max: 16 };
    }
    // Tablespoons
    if (u === 'tbsp') {
      return { min: 1, max: 8 };
    }
    // Teaspoons
    if (u === 'tsp') {
      return { min: 1, max: 12 };
    }
    // Handfuls
    if (u === 'handful') {
        return { min: 1, max: 5 };
    }
    // Default for discrete items (slice, egg, piece, patty, etc.)
    return { min: 1, max: 5 };
  };

  const addFood = (food) => {
    const { min, max } = getDefaultRanges(food.servingUnit);
    const newFood = {
      ...food,
      desiredMin: min,
      desiredMax: max,
      selectedServingUnit: food.servingUnit
    };
    const newSelectedFoods = [...selectedFoods, newFood];
    setSelectedFoods(newSelectedFoods);
    if (newSelectedFoods.length === 1) {
      onStepComplete();
    }
  };

  const removeFood = (foodId) => {
    setSelectedFoods(selectedFoods.filter(food => food.id !== foodId));
  };

  const updateFoodDesiredAmount = (foodId, field, value) => {
    setSelectedFoods(selectedFoods.map(food => (
      food.id === foodId ? { ...food, [field]: parseFloat(value) || 0 } : food
    )));
  };

  const updateServingUnit = (foodId, newUnit) => {
    setSelectedFoods(selectedFoods.map(food => {
      if (food.id === foodId) {
        // Calculate conversion factor: NewUnit -> OldUnit (to preserve physical amount)
        // Wait, convertValue(val, FROM, TO)
        const oldUnit = food.selectedServingUnit;
        
        // We want to convert the existing min/max values FROM oldUnit TO newUnit
        const newMin = convertValue(food.desiredMin, oldUnit, newUnit);
        const newMax = convertValue(food.desiredMax, oldUnit, newUnit);

        // Round nicely to avoid 100.0000001
        const round = (val) => {
             if (newUnit === 'g' || newUnit === 'ml') return Math.round(val);
             return Math.round(val * 100) / 100;
        };

        return { 
            ...food, 
            selectedServingUnit: newUnit,
            desiredMin: round(newMin),
            desiredMax: round(newMax)
        };
      }
      return food;
    }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Protein': 'bg-red-100 text-red-800',
      'Carbohydrate': 'bg-yellow-100 text-yellow-800',
      'Fat': 'bg-green-100 text-green-800',
      'Vegetable': 'bg-blue-100 text-blue-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getSourceIcon = (food) => {
    if (food.isCustom) return null;
    if (food.isExternal) {
      if (food.source === 'FatSecret') return <SafeIcon icon={FiStar} className="text-blue-500" />;
      return <SafeIcon icon={FiGlobe} className="text-blue-500" />;
    }
    return <SafeIcon icon={FiDatabase} className="text-gray-500" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden"
    >
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Select Foods</h2>
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-500 hover:text-gray-700"
          >
            <SafeIcon icon={isExpanded ? FiX : FiPlus} className="text-xl" />
          </button>
        </div>

        <div id="selected-foods-list" className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-600">Selected:</span>
          <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm font-semibold">
            {selectedFoods.length} foods
          </span>
        </div>

        {selectedFoods.length > 0 && (
          <div className="mb-4 space-y-3">
            {selectedFoods.map(food => (
              <motion.div 
                key={food.id}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="bg-indigo-50 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-indigo-900">{food.name}</h4>
                    <span className="text-sm text-indigo-600">{food.category}</span>
                    {food.isCustom && (
                      <span className="ml-2 bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full text-xs">Custom</span>
                    )}
                    {food.isExternal && (
                      <span className="ml-2 bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full text-xs">{food.source}</span>
                    )}
                    {food.brandName && (
                      <span className="ml-2 text-xs text-gray-500">{food.brandName}</span>
                    )}
                  </div>
                  <button 
                    onClick={() => removeFood(food.id)}
                    className="text-indigo-400 hover:text-indigo-600 hover:bg-indigo-200 rounded-full p-1"
                  >
                    <SafeIcon icon={FiX} className="text-sm" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-indigo-700 mb-1">Serving Unit</label>
                    <select 
                      value={food.selectedServingUnit} 
                      onChange={(e) => updateServingUnit(food.id, e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      {food.servingOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-indigo-700 mb-1">Min Amount</label>
                    <input 
                      type="number" 
                      value={food.desiredMin} 
                      onChange={(e) => updateFoodDesiredAmount(food.id, 'desiredMin', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-indigo-700 mb-1">Max Amount</label>
                    <input 
                      type="number" 
                      value={food.desiredMax} 
                      onChange={(e) => updateFoodDesiredAmount(food.id, 'desiredMax', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
                <div className="mt-2 text-xs text-indigo-600">
                  Range: {food.desiredMin} - {food.desiredMax} {food.selectedServingUnit}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-6"
          >
            <div className="flex items-center justify-center mb-4 bg-gray-100 rounded-lg p-1">
              <button 
                onClick={() => setSearchSource('local')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  searchSource === 'local' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <SafeIcon icon={FiDatabase} className="inline mr-2" />
                Local Database
              </button>
              <button 
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all text-gray-400 cursor-not-allowed`}
              >
                <SafeIcon icon={FiStar} className="inline mr-2" />
                FatSecret Foods <span className="ml-1 text-xs text-orange-500">(Coming Soon)</span>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400" />
                <input
                  id="food-selector-search"
                  type="text"
                  placeholder={searchSource === 'external' ? "Search FatSecret database..." : "Search local foods..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {isSearching && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent" />
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              <AnimatePresence>
                {filteredFoods.map(food => (
                  <motion.div
                    key={food.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-gray-800">{food.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(food.category)}`}>{food.category}</span>
                        {food.isCustom && (
                          <span className="bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full text-xs">Custom</span>
                        )}
                        {food.isExternal && (
                          <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full text-xs">{food.source}</span>
                        )}
                        {getSourceIcon(food)}
                      </div>
                      {food.brandName && (
                        <div className="text-xs text-gray-500 mb-1">{food.brandName}</div>
                      )}
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">{food.calories} cal</span> | 
                        <span className="ml-1">P: {food.protein}g</span> | 
                        <span className="ml-1">C: {food.carbs}g</span> | 
                        <span className="ml-1">F: {food.fat}g</span>
                        <span className="ml-1 text-gray-500">per {food.servingSize} {food.servingUnit}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {food.isCustom && user && (
                        <>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onEditCustomFood(food)}
                            className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-100 transition-colors"
                            title="Edit Food"
                          >
                            <SafeIcon icon={FiEdit2} className="text-sm" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onDeleteCustomFood(food.id)}
                            className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-100 transition-colors"
                            title="Delete Food"
                          >
                            <SafeIcon icon={FiTrash2} className="text-sm" />
                          </motion.button>
                        </>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => addFood(food)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-full transition-colors"
                      >
                        <SafeIcon icon={FiPlus} className="text-sm" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {filteredFoods.length === 0 && !isSearching && (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiSearch} className="text-4xl mx-auto mb-2 opacity-50" />
                  <p>
                    {searchTerm.length >= 2 && searchSource === 'external' 
                      ? 'No foods found. Try a different search term.' 
                      : searchSource === 'external' 
                        ? 'Type at least 2 characters to search FatSecret foods' 
                        : 'No foods found matching your criteria'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FoodSelector;